import React, { useState, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import MultiImage from '@components/MultiImage'
import Image from '@components/Image'
import { ImageUrl } from '@app-types/entity_types'

type Props = {
  image: ImageUrl
  modalImage: ImageUrl
  containerWidth: number
  containerHeight: number
  enableModalViewing: boolean
  className?: string
  style?: React.CSSProperties
  modalContainerSelector?: string
}
/*
  Note: We also have a generic ImageWithModal component, but this one is specifically used in the horizontal timeline context.
  It handles the image display and modal functionality for images within the timeline as when the timeline is fullscreened, the image is still displayed within the full-screened div.
  If we were to use the generic component, the image would be displayed in a modal outside of the timeline, and would not be visible when the timeline is viewed in fullscreen mode.
*/
const InTimelineImageWithModal: React.FC<Props> = props => {
  const {
    image,
    modalImage,
    containerWidth,
    containerHeight,
    enableModalViewing,
    className,
    style,
    modalContainerSelector = '#js-timeline-overlay',
  } = props

  const [modalOpen, setModalOpen] = useState(false)
  const portalTargetRef = useRef<null | HTMLElement>(null)

  const setupPortalTarget = () => {
    if (!portalTargetRef.current) {
      portalTargetRef.current = document.querySelector(modalContainerSelector)
      if (!portalTargetRef.current) {
        portalTargetRef.current = document.createElement('div')
        document.body.appendChild(portalTargetRef.current)
      }
    }
  }
  const toggleModalVisibility = () => {
    setModalOpen(!modalOpen)
  }

  const handleClick = () => {
    toggleModalVisibility()
  }

  // const handleCloseModal = () => {
  //   toggleModalVisibility()
  // }

  const getModalContent = () => {
    if (enableModalViewing && modalOpen) {
      let modalContent = null
      if (modalImage) {
        const imgStyle: React.CSSProperties = {
          width: '100%',
          height: 'auto',
          display: 'block',
        }
        modalContent = (
          <Image src={modalImage.src} width={modalImage.width} height={modalImage.height} style={imgStyle} />
        )
      } else {
        modalContent = <p>Sorry, this image is unavailable.</p>
      }
      const wrapperContent = (
        <div className="modal-image-wrapper">
          <div className="modal-image-container">{modalContent}</div>
        </div>
      )
      setupPortalTarget()
      if (portalTargetRef.current) {
        return createPortal(wrapperContent, portalTargetRef.current)
      }
    }
    return null
  }

  const imageStyle: React.CSSProperties = {
    cursor: enableModalViewing ? 'pointer' : 'inherit',
    ...style,
  }

  const onClick = enableModalViewing ? handleClick : undefined
  const modal = getModalContent()

  return (
    <div className={className} style={imageStyle} onClick={onClick}>
      <MultiImage
        imgSrc={image.src}
        imgWidth={image.width}
        imgHeight={image.height}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />
      {modal}
    </div>
  )
}

export default memo(InTimelineImageWithModal)
